<template>
  <div
    v-if="showUpdateBanner"
    class="app-update-banner d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-4"
  >
    <div>
      <div class="app-update-banner__title">Nova versão disponível</div>
      <div class="app-update-banner__text">
        Uma atualização do sistema foi encontrada. Atualize para carregar a
        versão mais recente.
      </div>
    </div>
    <div class="d-flex align-items-center gap-3">
      <button
        type="button"
        class="btn btn-sm btn-light"
        @click="dismissUpdateBanner"
      >
        Depois
      </button>
      <button
        type="button"
        class="btn btn-sm btn-warning"
        :disabled="updatingApp"
        @click="refreshApplication"
      >
        {{ updatingApp ? "Atualizando..." : "Atualizar agora" }}
      </button>
    </div>
  </div>
  <router-view />
</template>

<script lang="ts">
import {
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import { useStore } from "vuex";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import { themeMode } from "@/core/helpers/config";
import { initializeComponents } from "@/core/plugins/keenthemes";
import hubConnection from "@/utils/signalrHub";
import {
  applyAppUpdate,
  checkForAppUpdate,
  forceRefreshApp,
  PWA_UPDATE_AVAILABLE_EVENT,
} from "@/registerServiceWorker";

export default defineComponent({
  name: "app",
  setup() {
    const store = useStore();
    const showUpdateBanner = ref(false);
    const updatingApp = ref(false);
    let hasRefreshedAfterUpdate = false;
    let updateIntervalId: ReturnType<typeof setInterval> | null = null;
    let updateFallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const onUpdateAvailable = (_event?: Event) => {
      showUpdateBanner.value = true;
      updatingApp.value = false;
    };

    const onControllerChange = () => {
      if (hasRefreshedAfterUpdate) {
        return;
      }

      hasRefreshedAfterUpdate = true;
      if (updateFallbackTimeoutId) {
        window.clearTimeout(updateFallbackTimeoutId);
        updateFallbackTimeoutId = null;
      }
      window.location.reload();
    };

    const refreshApplication = async () => {
      updatingApp.value = true;
      showUpdateBanner.value = false;

      if (updateFallbackTimeoutId) {
        window.clearTimeout(updateFallbackTimeoutId);
      }

      updateFallbackTimeoutId = window.setTimeout(async () => {
        if (!hasRefreshedAfterUpdate) {
          await forceRefreshApp();
        }
      }, 4000);

      const startedUpdate = await applyAppUpdate();

      if (!startedUpdate) {
        if (updateFallbackTimeoutId) {
          window.clearTimeout(updateFallbackTimeoutId);
          updateFallbackTimeoutId = null;
        }
        await forceRefreshApp();
      }
    };

    const dismissUpdateBanner = () => {
      showUpdateBanner.value = false;
    };

    onMounted(() => {
      /**
       * this is to override the layout config using saved data from localStorage
       * remove this to use config only from static config (@/core/config/DefaultLayoutConfig.ts)
       */
      store.commit(Mutations.OVERRIDE_LAYOUT_CONFIG);

      store.dispatch(Actions.SET_THEME_MODE_ACTION, themeMode.value);

      nextTick(() => {
        initializeComponents();

        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      });

      hubConnection.start();

      if ("serviceWorker" in navigator) {
        window.addEventListener(PWA_UPDATE_AVAILABLE_EVENT, onUpdateAvailable);
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          onControllerChange
        );

        checkForAppUpdate();

        window.addEventListener("focus", checkForAppUpdate);
        document.addEventListener("visibilitychange", checkForAppUpdate);
        updateIntervalId = window.setInterval(() => {
          checkForAppUpdate();
        }, 5 * 60 * 1000);
      }
    });

    onBeforeUnmount(() => {
      if ("serviceWorker" in navigator) {
        window.removeEventListener(
          PWA_UPDATE_AVAILABLE_EVENT,
          onUpdateAvailable
        );
        navigator.serviceWorker.removeEventListener(
          "controllerchange",
          onControllerChange
        );
        window.removeEventListener("focus", checkForAppUpdate);
        document.removeEventListener("visibilitychange", checkForAppUpdate);
      }

      if (updateIntervalId) {
        window.clearInterval(updateIntervalId);
      }

      if (updateFallbackTimeoutId) {
        window.clearTimeout(updateFallbackTimeoutId);
      }
    });

    return {
      dismissUpdateBanner,
      refreshApplication,
      showUpdateBanner,
      updatingApp,
    };
  },
});
</script>

<style lang="scss">
@import "~bootstrap-icons/font/bootstrap-icons.css";
@import "~apexcharts/dist/apexcharts.css";
@import "~quill/dist/quill.snow.css";
@import "~animate.css";
@import "~sweetalert2/dist/sweetalert2.css";
@import "~nouislider/distribute/nouislider.css";
@import "~@fortawesome/fontawesome-free/css/all.min.css";
@import "~socicon/css/socicon.css";
@import "~line-awesome/dist/line-awesome/css/line-awesome.css";
@import "~dropzone/dist/dropzone.css";
@import "~@vueform/multiselect/themes/default.css";
@import "~prism-themes/themes/prism-shades-of-purple.css";
@import "~element-plus/dist/index.css";
@import "~shepherd.js/dist/css/shepherd.css";

@import "~@syncfusion/ej2-vue-grids/styles/material.css";
@import "~@syncfusion/ej2-base/styles/material.css";
@import "~@syncfusion/ej2-grids/styles/material.css";
@import "~@syncfusion/ej2-buttons/styles/material.css";
@import "~@syncfusion/ej2-popups/styles/material.css";
@import "~@syncfusion/ej2-navigations/styles/material.css";
@import "~@syncfusion/ej2-dropdowns/styles/material.css";
@import "~@syncfusion/ej2-lists/styles/material.css";
@import "~@syncfusion/ej2-inputs/styles/material.css";
@import "~@syncfusion/ej2-calendars/styles/material.css";
@import "~@syncfusion/ej2-splitbuttons/styles/material.css";

// Main demo style scss
@import "assets/sass/element-ui.dark";
@import "assets/sass/plugins";
@import "assets/sass/style";

// Dark mode demo style scss
//@import "assets/sass/plugins.dark";
//@import "assets/sass/style.dark";

//RTL version styles
//@import "assets/css/style.rtl.css";

#app {
  display: contents;
}

.app-update-banner {
  position: fixed;
  top: 1rem;
  right: 1rem;
  left: 1rem;
  z-index: 2000;
  padding: 1rem 1.25rem;
  border: 1px solid rgba(139, 104, 27, 0.25);
  border-radius: 1rem;
  background: linear-gradient(135deg, #fff7d6 0%, #ffe59a 100%);
  box-shadow: 0 18px 45px rgba(89, 67, 13, 0.18);
}

.app-update-banner__title {
  color: #5f4700;
  font-size: 0.95rem;
  font-weight: 700;
}

.app-update-banner__text {
  color: #735500;
  font-size: 0.875rem;
  margin-top: 0.15rem;
}
</style>
